import axios from 'axios';
import type { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios';
import { msalInstance } from '../config/msal';
import type { ApiErrorResponse } from '../types/api';

// Get base URL from environment
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://checkervzmr-app.azurewebsites.net';

/**
 * Create axios instance with default configuration
 */
export const apiClient: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120 seconds - increased for long-running operations like /evaluate
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Request interceptor to add authentication token
 */
apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        const account = accounts[0];

        // Send the ID token (audience = SPA client ID) so the backend can
        // validate it as proof-of-identity. The access token returned with
        // scopes:['User.Read'] has audience=Microsoft Graph and is not a
        // valid bearer for our API. If you later expose the API as a
        // dedicated Entra scope, swap to accessToken with that scope.
        const response = await msalInstance.acquireTokenSilent({
          scopes: ['openid', 'profile', 'email'],
          account: account,
        });

        if (response.idToken) {
          config.headers.Authorization = `Bearer ${response.idToken}`;
        }
      }
    } catch (error) {
      console.error('Error acquiring token:', error);
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

/**
 * Response interceptor to handle errors
 */
apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError<ApiErrorResponse>) => {
    // Handle different error status codes
    if (error.response) {
      const { status } = error.response;

      switch (status) {
        case 401: {
          // Don't force a logoutRedirect here — a single misconfigured /me
          // or /validations response would otherwise punt the user out
          // mid-session and create a sign-in → 401 → sign-out loop. Surface
          // the error and let the UI decide whether to prompt re-auth.
          console.warn('401 from API:', error.response.data);
          break;
        }

        case 403:
          // Forbidden - user doesn't have permission
          console.error('Access forbidden - insufficient permissions');
          break;

        case 404:
          // Not found
          console.error('Resource not found');
          break;

        case 500:
        case 502:
        case 503:
        case 504:
          // Server errors
          console.error('Server error:', error.response.data);
          break;

        default:
          console.error('API error:', error.response.data);
      }
    } else if (error.request) {
      // Request was made but no response received
      console.error('Network error - no response received');
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

/**
 * API Error class for better error handling
 */
export class ApiError extends Error {
  statusCode?: number;
  code?: string;
  details?: unknown;

  constructor(
    message: string,
    statusCode?: number,
    code?: string,
    details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
  }
}

/**
 * Extract error message from axios error
 */
export const getErrorMessage = (error: unknown): string => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<ApiErrorResponse>;

    if (axiosError.response?.data?.error) {
      return axiosError.response.data.error.message;
    }

    if (axiosError.response?.status === 401) {
      return 'Nemáte oprávnění k této akci. Přihlaste se prosím.';
    }

    if (axiosError.response?.status === 403) {
      return 'Nemáte dostatečná oprávnění pro tuto operaci.';
    }

    if (axiosError.response?.status === 404) {
      return 'Požadovaný zdroj nebyl nalezen.';
    }

    if (axiosError.response?.status && axiosError.response.status >= 500) {
      return 'Chyba serveru. Zkuste to prosím později.';
    }

    if (axiosError.code === 'ECONNABORTED') {
      return 'Požadavek vypršel. Zkuste to prosím znovu.';
    }

    if (axiosError.code === 'ERR_NETWORK') {
      return 'Chyba sítě. Zkontrolujte připojení k internetu.';
    }

    return axiosError.message || 'Neznámá chyba';
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'Něco se pokazilo. Zkuste to prosím znovu.';
};

/**
 * Type-safe API response wrapper
 */
export interface ApiResponse<T> {
  data: T;
  status: number;
  message?: string;
}

/**
 * Wrap API call with error handling
 */
export const apiCall = async <T>(
  promise: Promise<{ data: T; status: number }>
): Promise<ApiResponse<T>> => {
  try {
    const response = await promise;
    return {
      data: response.data,
      status: response.status,
    };
  } catch (error) {
    const message = getErrorMessage(error);
    throw new ApiError(
      message,
      axios.isAxiosError(error) ? error.response?.status : undefined,
      axios.isAxiosError(error) ? error.code : undefined,
      axios.isAxiosError(error) ? error.response?.data : undefined
    );
  }
};

export default apiClient;
